import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';

import { Card, CardHeader, CardContent, CardActions, Button, Switch, Grid, Divider } from '@mui/material';

import { blue, red } from '@mui/material/colors'

import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { DateTime } from 'luxon';

import { get, isEmpty, update } from 'lodash';

import { BasicFlex } from '../../utils/Common';

import Field from '../../utils/FormikFields/Field';

import TimePickerField from '../../utils/FormikFields/TimePicker';

import HorizontalRuleRoundedIcon from '@mui/icons-material/HorizontalRuleRounded';
import MoreTimeRoundedIcon from '@mui/icons-material/MoreTimeRounded';

import { theme } from '../../utils/theme';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { getWorkingHours, updateWorkingHours } from './apiService';

import useSnackbarStore from '../../store/snackbar';


const DayCardStyles = styled(Card)`
  width: 50rem;

  @media ${props => props.theme.breaks.down('md')} {
    width: 100%;
  }
`;

const SwitchField = styled.div`
  max-width: 5rem;

  @media ${props => props.theme.breaks.down('md')} {
    max-width: 4rem;
  }
`;

const DayLabel = styled.div`
  font-weight: 500;
  font-size: 1.6rem;
  color: ${props => props.theme.colors.mildText};
`;

const DayInfo = styled(BasicFlex)`
`

const TimeInfo = styled.div`
`

const TimeField = styled.div`
  min-width: 13rem;
  max-width: 13rem;

  & .MuiInputBase-root {
    font-size: 1.6rem;
  }

  & .MuiInputAdornment-root {
    /* width: 2.5rem; */
    margin-left: .2rem;
  }

  & .MuiButtonBase-root {
    padding: .5rem;
  }

  @media ${props => props.theme.breaks.down('md')} {
    min-width: 11rem;
    max-width: 11rem;

    & .MuiInputBase-root {
      font-size: 1.4rem;
    }

    & .MuiButtonBase-root {
      padding: .3rem;
    }
  }
`;

const DayCard = ({ day, label }) => {
  return (
    <DayCardStyles variant='outlined' sx={{borderRadius: '15px', paddingBottom: 0}}>
      <CardContent sx={{paddingBottom: '1.6rem !important'}}>
        <BasicFlex>
          <DayInfo justify="flex-start">
            <SwitchField>
              <Field name={`${day}.active`} formFieldType='switch' type="checkbox" />
            </SwitchField>
            <DayLabel>{label}</DayLabel>
          </DayInfo>
          <BasicFlex gap=".4rem">
            <TimeField>
              <TimePickerField name={`${day}.start_time`} styles={{backgroundColor: theme.colors.lightBg}} />
            </TimeField>
            <HorizontalRuleRoundedIcon fontSize='' />
            <TimeField>
              <TimePickerField name={`${day}.end_time`} styles={{backgroundColor: theme.colors.lightBg}} />
            </TimeField>
          </BasicFlex>
        </BasicFlex>
      </CardContent>
    </DayCardStyles>
  )
}

const WorkingHours = ({ stylist_id, get_public = false }) => {
  let default_start = DateTime.local().set({hour: 9, minute: 0, second: 0, millisecond: 0});
  let default_end = DateTime.local().set({hour: 17, minute: 0, second: 0, millisecond: 0});

  const [backend_data, setBackendData] = useState({});

  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['workingHours', stylist_id],
    queryFn: () => getWorkingHours(stylist_id),
  })

  let initialValues = {
    sunday: {day_of_week: 'sunday', active: false, start_time: default_start, end_time: default_end, blocks: []},
    monday: {day_of_week: 'monday', active: true, start_time: default_start, end_time: default_end, blocks: []},
    tuesday: {day_of_week: 'tuesday', active: true, start_time: default_start, end_time: default_end, blocks: []},
    wednesday: {day_of_week: 'wednesday', active: true, start_time: default_start, end_time: default_end, blocks: []},
    thursday: {day_of_week: 'thursday', active: true, start_time: default_start, end_time: default_end, blocks: []},
    friday: {day_of_week: 'friday', active: true, start_time: default_start, end_time: default_end, blocks: []},
    saturday: {day_of_week: 'saturday', active: false, start_time: default_start, end_time: default_end, blocks: []},
  };

  useEffect(() => {
    if (data?.length) {
      let newValues = {...initialValues};
      Object.keys(initialValues).forEach(day => {
        let day_data = data.find(d => d.day_of_week === day);
        if (day_data) {
          let start = DateTime.fromISO(day_data.start_time);
          let end = DateTime.fromISO(day_data.end_time);
          newValues[day] = {...day_data, start_time: start, end_time: end};
        }
      })
      setBackendData(newValues);
    }
  }, [data])

  const { mutate } = useMutation({
    mutationFn: updateWorkingHours,
    onSuccess: (data) => {
      showSnackbar('Updated successfully!', 'success');
      queryClient.invalidateQueries({ queryKey: ['stylists', stylist_id] });
    },
    onError: (error) => {
      console.log(error);
      showSnackbar('Something went wrong!', 'error');
    }
  })

  const onSubmit = async (values) => {
    try {
      mutate({stylist_id, userInfo: values})
    } catch (e) {
      
    }
  }
  return (
    <Formik
      initialValues={!isEmpty(backend_data) ? backend_data : initialValues}
      enableReinitialize
      onSubmit={onSubmit}
    >
      {(formikBag) => {
        return (
        <Form>
          <Card elevation={0} sx={{ borderRadius: "15px"}}>
            {/* <CardHeader title="Set Working Hours" /> */}
          <CardContent>
            <BasicFlex dir="column" gap="1.6rem">
              <DayCard day='sunday' label='Sunday' />
              <DayCard day='monday' label='Monday' />
              <DayCard day='tuesday' label='Tuesday' />
              <DayCard day='wednesday' label='Wednesday' />
              <DayCard day='thursday' label='Thursday' />
              <DayCard day='friday' label='Friday' />
              <DayCard day='saturday' label='Saturday' />
            </BasicFlex>
          </CardContent>
          <Divider />
          <CardActions sx={{justifyContent: "flex-end", p: 2}}>
            <Button startIcon={<MoreTimeRoundedIcon />} variant="outlined" size='large' type='submit'>Save Hours</Button>
          </CardActions>
          </Card>
        </Form>
          )
        }}
      </Formik>
  )
  
};

export default WorkingHours;